import {
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { Subject, takeUntil } from 'rxjs';

import { Store } from '@ngrx/store';

import {
	clearBulkFileUrl,
	clearBulkUploadResult,
	clearOrgBeneficiariesDataURL,
	fetchBulkActionFileUrlStart,
	fetchOrganizationById,
	fetchOrgBeneficiariesDataURLStart,
	uploadBulkActionFileStart,
} from '../../sanus/organizations/state/organizations.actions';
import {
	selectOrganizationById,
	selectOrgBeneficiariesDataURL,
	selectOrgUploadFileResult,
	selectXlsxUrl,
} from '../../sanus/organizations/state/organizations.selector';
import { FileUploadResult } from '../../shared/enums/file-upload-result.enum';
import { TemplateFileType } from '../../shared/enums/template-file.enum';
import { AuthenticationsService } from '../authentications/authentications.service';
import { AuthenticationLoginResponseDto } from '../authentications/dtos/authentication-login-response';
import { BulkActionDialogComponent } from '../beneficiaries/modals/bulk-action-upload-dialog/bulk-action-upload-dialog.component';
import { OrganizationsService } from '../organizations/organizations.service';

@Component({
	selector: 'app-bulk-actions',
	styleUrl: './bulk-actions.component.scss',
	templateUrl: './bulk-actions.component.html',
})
export class BulkActionsComponent implements OnInit, OnDestroy {
	@ViewChild('fileInputExclusion') fileInputExclusion?: ElementRef;
	@ViewChild('fileInputInclusion') fileInputInclusion?: ElementRef;
	@ViewChild('fileInputUpdate') fileInputUpdate?: ElementRef;

	actionType = TemplateFileType;
	isMatriz: boolean = false;
	orgId: string = '';
	preventMultipleDownloads = false;
	userEmail: string = '';

	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly store: Store,
		private readonly activatedRoute: ActivatedRoute,
		private readonly authenticationsService: AuthenticationsService,
		private readonly organizationsService: OrganizationsService,
		private readonly dialog: MatDialog,
		private readonly router: Router,
	) {}

	clearFileInputs(): void {
		if (this.fileInputInclusion) {
			this.fileInputInclusion.nativeElement.value = '';
		}

		if (this.fileInputUpdate) {
			this.fileInputUpdate.nativeElement.value = '';
		}

		if (this.fileInputExclusion) {
			this.fileInputExclusion.nativeElement.value = '';
		}
	}

	downloadFile(url: string): void {
		this.preventMultipleDownloads = true;
		const link = document.createElement('a');
		link.setAttribute('target', '_blank');
		link.setAttribute('href', url);
		link.setAttribute('download', 'file_name.xlsx');
		document.body.appendChild(link);
		link.click();
		link.remove();
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	ngOnInit(): void {
		this.activatedRoute.params.subscribe(({ id }) => {
			if (id) {
				this.orgId = id;
				this.store.dispatch(fetchOrganizationById({ id }));

				this.store
					.select(selectOrganizationById)
					.pipe(takeUntil(this.unsubscribe$))
					.subscribe((org) => {
						if (org) {
							this.isMatriz = org.is_matriz;
						}
					});
			}
		});

		const session: AuthenticationLoginResponseDto | null =
			this.authenticationsService.getSession();

		this.userEmail = session?.email || '';

		this.store
			.select(selectXlsxUrl)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((url) => {
				if (url && !this.preventMultipleDownloads) {
					this.downloadFile(url);
					this.store.dispatch(clearBulkFileUrl());
				}
			});

		this.store
			.select(selectOrgBeneficiariesDataURL)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((url) => {
				if (url && !this.preventMultipleDownloads) {
					this.downloadFile(url);
					this.store.dispatch(clearOrgBeneficiariesDataURL());
				}
			});

		this.store
			.select(selectOrgUploadFileResult)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((result) => {
				if (result === FileUploadResult.SUCCESS) {
					const ref = this.dialog.open(BulkActionDialogComponent);
					ref.afterClosed().subscribe((navigate) => {
						if (navigate) {
							this.router.navigate([
								'organizacoes',
								this.orgId,
								'acoes-em-massa',
							]);
						}
					});
					this.store.dispatch(clearBulkUploadResult());
				}
			});
	}

	onActiveDataDownload(isMatriz?: boolean): void {
		this.preventMultipleDownloads = false;
		this.store.dispatch(
			fetchOrgBeneficiariesDataURLStart({
				isMatriz,
				orgId: this.orgId,
			}),
		);
	}

	onFileDownload(actionType: TemplateFileType): void {
		this.preventMultipleDownloads = false;
		this.store.dispatch(
			fetchBulkActionFileUrlStart({ actionType, orgId: this.orgId }),
		);
	}

	onFileUpload(actionType: TemplateFileType, event: any): void {
		const request: FormData = new FormData();

		if (this.validFile(event.target.files.item(0))) {
			request.append('excel', event.target.files.item(0));
			request.append('email', this.userEmail);
			request.append('organizationId', this.orgId);
			this.store.dispatch(uploadBulkActionFileStart({ actionType, request }));
			this.clearFileInputs();
		}
	}

	validFile(file: File): boolean {
		const ext = file?.name.split('.').pop();

		return ext === 'xlsx';
	}
}
